import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

interface UseVendorsOptions {
  featured?: boolean;
  limit?: number;
}

interface Vendor {
  id: string;
  store_name: string;
  description: string;
  email: string;
  phone: string;
  borough?: string;
  banner_image?: string;
  logo_image?: string;
  rating?: number;
  total_products?: number;
}

export function useVendors(options: UseVendorsOptions = {}) {
  const { featured, limit } = options;

  return useQuery({
    queryKey: ['vendors', { featured, limit }],
    queryFn: async () => {
      // Get active vendors that are not deleted
      let query = supabase
        .from('vendors')
        .select(`
          *,
          products:products(
            id,
            title,
            price,
            images,
            status,
            quantity
          )
        `)
        .eq('status', 'active')
        .not('is_deleted', 'eq', true)
        .order('created_at', { ascending: false });
      
      // Apply limit if specified
      if (limit) {
        query = query.limit(limit);
      }

      const { data: vendors, error: vendorsError } = await query;

      if (vendorsError) throw vendorsError;

      // Filter out deleted products and zero quantity products from each vendor
      const vendorsWithActiveProducts = vendors?.map(vendor => ({
        ...vendor,
        products: (vendor.products || []).filter(product => 
          product.status === 'active' && product.quantity > 0
        )
      })) || [];

      // If featured is true, only return vendors with high ratings (4+)
      if (featured) {
        return vendorsWithActiveProducts.filter(vendor => 
          vendor.rating && vendor.rating >= 4
        ).slice(0, limit || 3); // Default limit to 3 for featured vendors
      }

      return vendorsWithActiveProducts;
    },
  });
} 